import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getCustomAttributesTypes } from 'lib/customAttributes';
import { Badge, EmptyIndicator, Link } from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { Icon } from 'components/common/icons';

import type { GetUserCustomAttributesRes } from 'shared-types/responses';

interface Props {
  userCustomAttributes: GetUserCustomAttributesRes['userCustomAttributes'];
}

const UserAttributesLimitedList = ({ userCustomAttributes }: Props) => {
  const { language, messages, theme } = useReduxState();

  return (
    <div className="user-attributes-list">
      <Box padding={0} title={messages.userAttributes}>
        {userCustomAttributes?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell></BoxTableCell>
                <BoxTableCell>{messages.commonAttribute}</BoxTableCell>
                <BoxTableCell>
                  {messages.userAttributesFormApplies}
                </BoxTableCell>
                <BoxTableCell>{messages.commonType}</BoxTableCell>
                <BoxTableCell>{messages.commonRequired}</BoxTableCell>
              </BoxTableHeader>

              {userCustomAttributes.map((attribute) => (
                <BoxTableRow key={attribute.id}>
                  <BoxTableCell shrink={true} style={{ paddingRight: 0 }}>
                    <div
                      className="enabled"
                      aria-label={
                        attribute.isEnabled
                          ? messages.commonEnabled
                          : messages.commonDisabled
                      }
                      data-tooltip={
                        attribute.isEnabled
                          ? messages.commonEnabled
                          : messages.commonDisabled
                      }
                    >
                      <Icon
                        icon="check-circle"
                        style={{
                          color: attribute.isEnabled
                            ? theme.successStandaloneColor
                            : 'rgba(0, 0, 0, 0.15)'
                        }}
                      />
                    </div>
                  </BoxTableCell>
                  <BoxTableCell>
                    <Link
                      href={`/admin/settings/user-attributes/${attribute.id}`}
                      linkColor="inherit"
                      text={
                        attribute.titleTranslations?.[language] ||
                        attribute.title
                      }
                    />

                    {attribute.description ? (
                      <div className="subtitle">
                        {attribute.descriptionTranslations?.[language] ||
                          attribute.description}
                      </div>
                    ) : null}
                  </BoxTableCell>
                  <BoxTableCell>
                    <div className="badge-container">
                      {[
                        {
                          text: messages.commonUser,
                          value: attribute.appliesTo?.user
                        },
                        {
                          text: messages.usersAdmin,
                          value: attribute.appliesTo?.admin
                        },
                        {
                          text: messages.usersGuest,
                          value: attribute.appliesTo?.guest
                        }
                      ]
                        .filter((item) => item.value)
                        .map((item) => (
                          <Badge
                            key={item.text}
                            size="small"
                            text={item.text}
                          />
                        ))}
                    </div>
                    {attribute.conditions?.length ? (
                      <div className="subtitle">
                        {`${attribute.conditions.length} ${messages.userAttributesConditions}`}
                      </div>
                    ) : null}
                  </BoxTableCell>
                  <BoxTableCell>
                    {getCustomAttributesTypes({ messages })[attribute.type]}
                    {attribute.type === 'Number' ? (
                      <div className="subtitle">
                        {`${attribute.min}-${attribute.max}`}
                      </div>
                    ) : attribute.type === 'ArrayWithOptions' ||
                      attribute.type === 'StringWithOptions' ? (
                      <div className="subtitle">
                        {attribute.optionGroups?.length > 1
                          ? getMessageWithPlaceholders({
                              message: messages.commonNumberOfOptionGroups,
                              placeholders: {
                                numberOfOptionGroups:
                                  attribute.optionGroups.length
                              }
                            })
                          : getMessageWithPlaceholders({
                              message: messages.commonNumberOfOptions,
                              placeholders: {
                                numberOfOptions:
                                  attribute.optionGroups?.[0]?.options.length
                              }
                            })}
                      </div>
                    ) : null}
                  </BoxTableCell>
                  <BoxTableCell>
                    {attribute.isRequired?.value &&
                    attribute.isRequired?.conditions?.length
                      ? `${messages.commonYes}*`
                      : attribute.isRequired?.value
                      ? messages.commonYes
                      : messages.commonNo}
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator icon="tag" title={messages.attributesEmpty} />
        )}
      </Box>

      <style jsx>{`
        .subtitle {
          color: ${theme.textColorLighter};
          font-size: 1.2rem;
          font-weight: 400;
          margin-top: 2px;
          white-space: nowrap;
        }
        .badge-container {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
        }
      `}</style>
    </div>
  );
};

export default UserAttributesLimitedList;
